import { useDeepCompareEffect } from "@react-hookz/web";
import { useAtom } from "jotai";
import type { UseApiLoaderResponse } from "../../../server/api/hooks/use-api-loader";
import { useApiLoader } from "../../../server/api/hooks/use-api-loader";
import { useToken } from "../../../server/auth/use-token";
import { authHeader } from "../../../utils/auth-header";
import { selfDriving } from "../../self-driving/self-driving";
import { multiformatAtom } from "../store/multiformat-atom";
import type { Multiformat } from "../store/types";

/**
 * If you're using multiformat atoms on your page, you MUST use this hook at the page level.
 * After that, you can use the `multiformatAtom` atom to access the data.
 */
export const useMultiformat = (): UseApiLoaderResponse<Multiformat | null> => {
  const [multiformat, setMultiformat] = useAtom(multiformatAtom);
  const getToken = useToken();

  const apiLoader = useApiLoader(async () => {
    if (multiformat.data) return multiformat.data;

    const { error, data } = await selfDriving.GET("/multiformat", {
      ...authHeader(await getToken()),
    });

    if (error) {
      console.error(error);
      throw new Error("Cannot get /multiformat");
    }

    return { ...data };
  }, [getToken, multiformat.data]);

  useDeepCompareEffect(() => {
    if (!multiformat || !apiLoader.loading) {
      setMultiformat(apiLoader);
    }
  }, [apiLoader.loading]);

  return apiLoader;
};
